/* eslint-disable @wix/da/hardcoded-urls */
import type { ReactNode, FC } from 'react';
import { useContext } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  ThemeContext,
  ThemeOptions,
} from '@wix/da-react-context/pkg/ThemeContext';
import c from 'classnames';
import { Url, URLS, sanitizeHref } from '@wix/da-url';
import s from './Policy.scss';

export interface Props {
  i18nKey?: string;
  children?: ReactNode;
}

const PolicyPage: FC<Props> = ({ i18nKey, children }) => {
  const { t } = useTranslation('about');
  const currentTheme = useContext(ThemeContext);
  return (
    <div className={c(s['page'], s['legacy'], s['journal'])}>
      {i18nKey ? (
        <Trans
          t={t}
          i18nKey={i18nKey}
          components={{
            h1: <h1 />,
            h2: <h2 />,
            h3: <h3 />,
            h4: <h4 />,
            h5: <h5 />,
            p: <p />,
            b: <b />,
            i: <i />,
            u: <u />,
            em: <em />,
            ol: <ol />,
            ul: <ul />,
            li: <li />,
            table: <table />,
            thead: <thead />,
            tbody: <tbody />,
            tr: <tr />,
            th: <th />,
            td: <td />,
            'link-about-copyright': (
              <a href={`${URLS.ABOUT}/policy/copyright/`} />
            ),
            'link-about-etiquette': (
              <a href={`${URLS.ABOUT}/policy/etiquette/`} />
            ),
            'link-about-points': <a href={`${URLS.ABOUT}/policy/points/`} />,
            'link-about-privacy': <a href={`${URLS.ABOUT}/policy/privacy/`} />,
            'link-about-submit': (
              <a href={`${URLS.ABOUT}/policy/submission/`} />
            ),
            'link-about-tos': <a href={`${URLS.ABOUT}/policy/service/`} />,
            'link-dreamup-service': <a href={Url.dreamupLink()} />,
            'link-dreamup-terms': <a href={URLS.DREAMUP_POLICY} />,
            'link-dreamup-stable-diffusion-license': (
              <a href="https://huggingface.co/stabilityai/stable-diffusion-xl-refiner-1.0/blob/main/LICENSE.md" />
            ),
            'link-email-help': <a href="mailto:help@deviantart.com" />,
            'link-email-media': <a href="mailto:media@deviantart.com" />,
            'link-email-violations': (
              <a href="mailto:violations@deviantart.com" />
            ),
            'link-int-commintro': (
              <a href="https://www.deviantart.com/danlev/journal/Introducing-Commissions-on-deviantART-347788178" />
            ),
            'link-int-cookies': <a href={URLS.COOKIES} />,
            'link-int-core': <a href={URLS.CORE_MEMBERSHIP} />,
            'link-int-core-dreamup': (
              <a
                href={`${URLS.CORE_MEMBERSHIP}?referer=https://www.deviantart.com/dreamup`}
              />
            ),
            'link-int-gdpr': (
              <a href="https://www.deviantart.com/settings/gdpr/" />
            ),
            'link-int-joincopyright': (
              <a href={`${Url.joinLink()}?joinpoint=copyright`} />
            ),
            'link-int-jointerms': (
              <a href={`${Url.joinLink()}?joinpoint=Terms`} />
            ),
            'link-int-pcpintro': (
              <a href="https://www.deviantart.com/heidi/journal/Introducing-the-Premium-Content-Platform-313613925" />
            ),
            'link-int-points': <a href={URLS.POINTS} />,
            'link-int-root': <a href={URLS.ROOT} />,
            'link-int-settings': <a href={URLS.SETTINGS_GENERAL} />,
            'link-int-prints': <a href={URLS.SHOP} />,
            'link-int-stash': <a href={URLS.STASH} />,
            'link-kb-adopt': (
              <a href="https://www.deviantartsupport.com/en/adoptables" />
            ),
            'link-kb-commterms': (
              <a href="https://www.deviantartsupport.com/en/buying-and-selling/commissions" />
            ),
            'link-kb-copyright': <a href={URLS.COPYRIGHT_POLICY} />,
            'link-kb-counterdmca': <a href={Url.contactDmcaCounterLink()} />,
            'link-kb-dmca': <a href={Url.contactDmcaHolderLink()} />,
            'link-kb-exclusives-resell': (
              <a href="https://www.deviantartsupport.com/en/article/how-do-i-resell-an-exclusive-ive-bought" />
            ),
            'link-kb-newticket': <a href={Url.contactLink()} />,
            'link-kb-paymethods': (
              <a href="https://www.deviantartsupport.com/en/article/what-payment-methods-does-deviantart-support" />
            ),
            'link-kb-pcpterms': (
              <a href="https://www.deviantartsupport.com/en/article/default-premium-content-platform-seller-agreement" />
            ),
            'link-kb-pdterms': (
              <a href="https://www.deviantartsupport.com/en/buying-and-selling/premium-downloads" />
            ),
            'link-kb-pgterms': (
              <a href="https://www.deviantartsupport.com/en/buying-and-selling/premium-galleries-1627539" />
            ),
            'link-kb-points': (
              <a href="https://www.deviantartsupport.com/en/article/what-are-points-1749105" />
            ),
            'link-kb-printhowto': (
              <a href="https://www.deviantartsupport.com/en/article/how-do-i-submit-my-deviation-as-a-print" />
            ),
            'link-kb-printprices': (
              <a href="https://www.deviantartsupport.com/en/article/what-are-the-base-prices-for-prints" />
            ),
            'link-kb-servicefees': (
              <a
                href={`${URLS.HELP}/article/what-are-the-costs-for-withdrawing-your-earnings-from-deviantart`}
              />
            ),
            'link-kb-subscriptions': (
              <a href="https://www.deviantartsupport.com/en/subscriptions/creating" />
            ),
            'link-kb-withdrawearnings': (
              <a href="https://www.deviantartsupport.com/en/article/how-do-i-withdraw-my-earnings" />
            ),
            'link-ext-adoptout': (
              <a href="https://optout.networkadvertising.org/?c=1" />
            ),
            'link-ext-bitpay': (
              <a href="https://bitpay.com/legal/terms-of-use/" />
            ),
            'link-ext-bitpayrefund': (
              <a href="https://support.bitpay.com/hc/en-us/articles/360000051746-How-to-claim-a-refund-from-a-BitPay-merchant-refund-email" />
            ),
            'link-ext-cclicenses': (
              <a href="http://creativecommons.org/licenses/" />
            ),
            'link-ext-chillingeffects': (
              <a href="https://www.chillingeffects.org/topics/22" />
            ),
            'link-ext-efffairuse': (
              <a href="http://w2.eff.org/IP/eff_fair_use_faq.php" />
            ),
            'link-ext-fsprivacy': (
              <a href="https://freestar.com/privacy-policy/" />
            ),
            'link-ext-googleprivacy': (
              <a href="https://policies.google.com/privacy?hl=en-US" />
            ),
            'link-ext-harvardfairuse': (
              <a href="http://cyber.law.harvard.edu/fairuse/" />
            ),
            'link-ext-mpegla': <a href="https://www.mpegla.com/" />,
            'link-ext-newrelic': (
              <a href="https://newrelic.com/termsandconditions/privacy" />
            ),
            'link-ext-perimeterx': (
              <a href="https://www.perimeterx.com/legal/privacy/" />
            ),
            'link-ext-privacyshield': (
              <a href="https://www.privacyshield.gov/" />
            ),
            'link-ext-protectprotocol': (
              <a href="https://www.protectprotocol.io/" />
            ),
            'link-ext-stanfordfairuse': (
              <a href="http://fairuse.stanford.edu/Copyright_and_Fair_Use_Overview/chapter9/" />
            ),
            'link-ext-wixprivacy': (
              <a href="https://www.wix.com/about/privacy" />
            ),
            'link-ext-huggingface': (
              <a href="https://huggingface.co/spaces/CompVis/stable-diffusion-license" />
            ),
            'link-1': <a href={sanitizeHref(t('policy.link1Url'))} />,
            'link-2': <a href={sanitizeHref(t('policy.link2Url'))} />,
            'link-3': <a href={sanitizeHref(t('policy.link3Url'))} />,
            'link-4': <a href={sanitizeHref(t('policy.link4Url'))} />,
            'link-5': <a href={sanitizeHref(t('policy.link5Url'))} />,
            'link-6': <a href={sanitizeHref(t('policy.link6Url'))} />,
            'link-7': <a href={sanitizeHref(t('policy.link7Url'))} />,
            'link-8': <a href={sanitizeHref(t('policy.link8Url'))} />,
            'link-9': <a href={sanitizeHref(t('policy.link9Url'))} />,
            'link-10': <a href={sanitizeHref(t('policy.link10Url'))} />,
            'link-11': <a href={sanitizeHref(t('policy.link11Url'))} />,
            'link-12': <a href={sanitizeHref(t('policy.link12Url'))} />,
            'link-13': <a href={sanitizeHref(t('policy.link13Url'))} />,
            'link-14': <a href={sanitizeHref(t('policy.link14Url'))} />,
            'link-15': <a href={sanitizeHref(t('policy.link15Url'))} />,
            'link-16': <a href={sanitizeHref(t('policy.link16Url'))} />,
            'link-17': <a href={sanitizeHref(t('policy.link17Url'))} />,
            'link-18': <a href={sanitizeHref(t('policy.link18Url'))} />,
            'link-19': <a href={sanitizeHref(t('policy.link19Url'))} />,
            'link-20': <a href={sanitizeHref(t('policy.link20Url'))} />,
            lastUpdated: <>{t('accessibility.lastUpdated')}</>,
            'logo-dreamup': (
              <img
                src={
                  currentTheme === ThemeOptions.DARK
                    ? 'https://st.deviantart.net/dreamup/dreamup-logo-light.png'
                    : 'https://st.deviantart.net/dreamup/dreamup-logo-dark.png'
                }
                alt="DreamUp"
                className={s['logo']}
              />
            ),
          }}
        />
      ) : (
        children
      )}
    </div>
  );
};
PolicyPage.displayName = 'PolicyPage';
export default PolicyPage;
